import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import ReviewModal from './ReviewModal';
import api from '../services/api';

const AdminPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Admin: Fetching reviews...');
      
      const data = await api.getReviews({});
      console.log('✅ Admin: Reviews fetched:', data);
      
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Admin: Error fetching reviews:', err);
      setError(`Kunne ikke hente anmeldelser: ${err.message}`);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = () => {
    setEditingReview(null);
    setShowModal(true);
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowModal(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Er du sikker på at du vil slette denne anmeldelse?')) {
      try {
        console.log('🗑️ Deleting review:', reviewId);
        await api.deleteReview(reviewId);
        console.log('✅ Review deleted');
        await fetchReviews();
      } catch (err) {
        console.error('❌ Error deleting review:', err);
        setError(`Kunne ikke slette anmeldelse: ${err.message}`);
      }
    }
  };

  const handleSaveReview = async (reviewData) => {
    try {
      console.log('💾 Saving review:', reviewData);
      setError(null);
      
      if (editingReview) {
        await api.updateReview(editingReview.id, reviewData);
        console.log('✅ Review updated');
      } else {
        await api.createReview(reviewData);
        console.log('✅ Review created');
      }
      
      setShowModal(false);
      setEditingReview(null);
      await fetchReviews();
    } catch (err) {
      console.error('❌ Error saving review:', err);
      setError(`Kunne ikke gemme anmeldelse: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('da-DK');
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '40px' }}>
      {/* Admin Header */}
      <div style={{
        background: 'var(--primary-gradient)',
        padding: '60px 0',
        marginBottom: '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <a 
              href="/" 
              className="btn"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <ArrowLeft size={18} />
              Tilbage til blog
            </a>
            
            <button
              onClick={handleCreateReview}
              className="btn"
              style={{
                background: 'white',
                color: '#667eea',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus size={18} />
              Ny anmeldelse
            </button>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: '3rem',
              fontWeight: '900',
              color: 'white',
              marginBottom: '12px',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              Admin Panel
            </h1>
            <p style={{
              fontSize: '1.2rem',
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: '300'
            }}>
              Administrer dine musikanmeldelser
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {error && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            background: 'rgba(255, 107, 107, 0.1)', 
            borderRadius: '20px',
            color: '#ff6b6b',
            marginBottom: '20px',
            border: '1px solid rgba(255, 107, 107, 0.3)'
          }}>
            <p>⚠️ {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">
            <p>Indlæser anmeldelser...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="empty-state">
            <h3>Ingen anmeldelser endnu</h3>
            <p>Opret din første anmeldelse for at komme i gang</p>
            <button
              onClick={handleCreateReview}
              className="btn btn-primary"
              style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px', margin: '20px auto 0' }}
            >
              <Plus size={18} />
              Opret første anmeldelse
            </button>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'grid',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {reviews.map((review) => (
                <div key={review.id} className="card">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '20px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <span className={`badge badge-${review.type}`}>
                          {review.type}
                        </span>
                        <div className="rating">
                          {review.rating}/10
                        </div>
                      </div>
                      
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        {review.title}
                      </h3>
                      
                      <p style={{
                        fontSize: '1.1rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '12px'
                      }}>
                        {review.artist}
                      </p>
                      
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'var(--text-muted)',
                        marginBottom: '16px'
                      }}>
                        {review.release_date && `Udgivet: ${formatDate(review.release_date)}`}
                        {review.concert_date && `Koncert: ${formatDate(review.concert_date)}`}
                        {review.venue && ` • ${review.venue}`}
                      </div>
                      
                      <p style={{
                        color: 'var(--text-secondary)',
                        lineHeight: '1.6'
                      }}>
                        {review.review_text.substring(0, 200)}
                        {review.review_text.length > 200 && '...'}
                      </p>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginLeft: '20px'
                    }}>
                      <button
                        onClick={() => handleEditReview(review)}
                        className="btn btn-secondary"
                        style={{ padding: '12px' }}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="btn btn-danger"
                        style={{ padding: '12px' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <ReviewModal
          review={editingReview}
          onSave={handleSaveReview}
          onClose={() => {
            setShowModal(false);
            setEditingReview(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminPage;
